/**
 * ai-chips.ts — per-step quick-question chips for the AI assistant panel.
 *
 * Each ChipDef has:
 *   question     — the text sent to the AI (also shown as the chip label)
 *   staticAnswer — shown IMMEDIATELY when the chip is clicked, before the
 *                  AI responds.  This makes the product feel instant even
 *                  when the AI is streaming.  When AI is unavailable, only
 *                  the staticAnswer is shown.
 *
 * Keeping static answers here (not in the backend) means the frontend
 * feels responsive with zero network round-trips for common questions.
 */

export interface ChipDef {
  question: string;
  staticAnswer: string;
}

/** Map of wizard step slug → chips to show in that step. */
export const STEP_CHIPS: Record<string, ChipDef[]> = {
  personal: [
    {
      question: "What is an A-Number and where do I find it?",
      staticAnswer:
        "Your A-Number (Alien Registration Number) is printed on the front of your Green Card as \"USCIS#\" or \"A-Number\". It's 7–9 digits. Enter just the digits on the N-400, without the leading \"A\".",
    },
    {
      question: "Which LPR date do I use — card issue date or status grant date?",
      staticAnswer:
        "Use the \"Resident Since\" date printed on your Green Card — the date USCIS originally approved your permanent residence. This is not the card's expiry date or the date it was printed. If you've renewed your card, use the original date from your first card.",
    },
    {
      question: "How do I know which naturalization basis applies to me?",
      staticAnswer:
        "Most applicants use the 5-year general rule (5 years as an LPR, 30 months of physical presence in those 5 years). If you have been continuously married to and living with a U.S. citizen for 3 years and have been an LPR for 3 years, you may qualify under the 3-year rule. If you are uncertain which basis applies to your specific situation, check uscis.gov/n-400 or consult an immigration attorney before filing.",
    },
    {
      question: "What if I don't have a Social Security Number?",
      staticAnswer:
        "The SSN field is optional on the N-400. Leave it blank if you don't have one — not having an SSN does not affect your eligibility for naturalization.",
    },
  ],

  biographic: [
    {
      question: "Why does USCIS ask about race and ethnicity?",
      staticAnswer:
        "USCIS collects race and ethnicity data for statistical reporting purposes only. Your answers do not affect your eligibility for naturalization in any way.",
    },
    {
      question: "What if none of the race categories fit me?",
      staticAnswer:
        "Select all that apply. If none fit exactly, choose the closest match. There is no wrong answer here — this is demographic data only and has no impact on your application.",
    },
    {
      question: "How do I convert my height from feet to centimeters?",
      staticAnswer:
        "Multiply feet by 30.48, then add inches × 2.54. Example: 5'7\" = (5 × 30.48) + (7 × 2.54) = 152.4 + 17.78 = 170 cm. Or just search \"5 feet 7 inches in cm\" in any search engine.",
    },
  ],

  addresses: [
    {
      question: "What if I don't remember an exact address from 5 years ago?",
      staticAnswer:
        "Do your best. Approximate addresses with estimated dates are acceptable — USCIS understands that people move frequently. Check old bank statements, tax returns, or Google Maps history to help remember. A good-faith effort matters more than perfection.",
    },
    {
      question: "What's the difference between a mailing and physical address?",
      staticAnswer:
        "Physical address = where you actually live. Mailing address = where you receive mail (can be the same, or a P.O. box, or a family member's address). USCIS sends interview notices and decisions to your mailing address.",
    },
    {
      question: "Do I need a full 5 years of addresses with no gaps?",
      staticAnswer:
        "Yes — USCIS asks for a complete address history for the past 5 years with no gaps. If you moved between addresses with no overlap, the end date of one should be the start date of the next.",
    },
  ],

  employment: [
    {
      question: "Do I need to list every job for the past 5 years?",
      staticAnswer:
        "Yes — USCIS asks for a complete 5-year employment history with no gaps. If you held multiple jobs, list each one. If you were unemployed, add an entry with Employer = \"Unemployed\" and the dates you were out of work.",
    },
    {
      question: "What if I had gaps in employment?",
      staticAnswer:
        "Add an \"Unemployed\" entry for each gap period. There's no penalty for gaps — USCIS just wants the history to be complete.",
    },
    {
      question: "How do I list self-employment or freelance work?",
      staticAnswer:
        "Enter your own name or business name as the employer, check \"Self-employed\", and list your occupation title as you normally would. You don't need exact client names.",
    },
  ],

  travel: [
    {
      question: "Is a 6-month trip outside the U.S. a problem for my application?",
      staticAnswer:
        "A single trip of 180 days or more may be considered a break in continuous residence. If this applies to you, you will need to demonstrate to USCIS that you maintained your U.S. residence during the absence — through evidence such as a U.S. job, maintained home, or immediate family remaining in the U.S. List the trip accurately. For any trip of 180 days or more, consider consulting an immigration attorney before filing to assess your specific situation.",
    },
    {
      question: "What is continuous residence and how is it different from physical presence?",
      staticAnswer:
        "Continuous residence means you've maintained your permanent home in the U.S. during the required period (5 or 3 years). Physical presence counts the actual days you were inside the U.S. — you need 30 months for the 5-year rule, 18 months for the 3-year rule. A single long trip can break continuous residence even if your total physical presence is fine.",
    },
    {
      question: "Do I need to list every short trip, including weekend trips?",
      staticAnswer:
        "Yes — list every trip outside the U.S., even 1-day trips to Canada or Mexico. The rules engine uses this data to calculate your physical presence. Short trips don't affect eligibility, but the history needs to be complete.",
    },
    {
      question: "What is the 30-month physical presence rule?",
      staticAnswer:
        "For the 5-year rule, you must have been physically inside the U.S. for at least 30 months (about 2.5 years) out of the 5 years before filing. Every day you're inside the U.S. counts. Days of departure and return both count as days outside.",
    },
  ],

  marriage: [
    {
      question: "If I'm married to a U.S. citizen, can I apply after 3 years instead of 5?",
      staticAnswer:
        "Yes — if you've been a permanent resident for at least 3 years AND have been married to and living with a U.S. citizen for those 3 years, you may qualify under the 3-year rule. Select \"3-year (spouse of USC)\" as your naturalization basis. You still need 18 months of physical presence in those 3 years.",
    },
    {
      question: "Do I need to list marriages that ended in divorce?",
      staticAnswer:
        "Yes — all prior marriages must be listed, regardless of how they ended. This is required even if the marriage was very brief. Having prior divorces does not negatively affect your application.",
    },
    {
      question: "What's the difference between my spouse being a citizen \"by birth\" vs. \"naturalized\"?",
      staticAnswer:
        "\"By birth\" means your spouse was born a U.S. citizen — either born in the U.S., or born abroad to U.S. citizen parents. \"Naturalized\" means they became a citizen through the naturalization process (filed N-400 and took the oath). Check their U.S. passport or Certificate of Naturalization if you're not sure.",
    },
  ],

  children: [
    {
      question: "Do I need to list children who are already U.S. citizens?",
      staticAnswer:
        "Yes — list all children regardless of their citizenship status, age, or where they live. This includes children who are already U.S. citizens. Having U.S. citizen children is not a problem and doesn't need explanation.",
    },
    {
      question: "Do stepchildren count?",
      staticAnswer:
        "Yes — include all stepchildren from your marriage(s). List their relationship as \"Step\".",
    },
    {
      question: "What about children living abroad?",
      staticAnswer:
        "Yes — list them. Children living outside the U.S. are still included. Answer \"No\" to \"lives with applicant\" for those children.",
    },
  ],

  criminal: [
    {
      question: "Does a dismissed charge or expunged record count?",
      staticAnswer:
        "Yes — the N-400 criminal history question is intentionally broad. You must disclose arrests even if charges were dropped, dismissed, or expunged. When in doubt, disclose. Omitting a known arrest is more damaging than disclosing one that turns out to be minor. For any actual criminal history, consult an immigration attorney.",
    },
    {
      question: "What does \"committed a crime for which you were not arrested\" mean?",
      staticAnswer:
        "This question asks whether you committed an act you knew was a crime, even if you were never caught or charged. It is intentionally broad. If anything in your past gives you pause — however minor it may seem — consult a licensed immigration attorney before answering. An honest disclosure with context is far better than an omission discovered later.",
    },
    {
      question: "What is Selective Service and how do I find my registration number?",
      staticAnswer:
        "Selective Service is the U.S. system for tracking potential military conscription. Males born after 12/31/1959 who lived in the U.S. between ages 18–26 were required to register. Check your status and find your number at sss.gov. If you were required to register but didn't, consult an immigration attorney before filing.",
    },
    {
      question: "Does filing taxes late count as \"failing to file\"?",
      staticAnswer:
        "Filing a return late — even years late — is generally considered different from willfully failing to file. Late filers have typically satisfied their legal obligation by filing. If you have completely unfiled years for which you were required to file, consider addressing them before applying. In either case, consult an immigration attorney about how your specific tax history may affect your application.",
    },
  ],

  documents: [
    {
      question: "What documents do I absolutely need to submit?",
      staticAnswer:
        "Required for all applicants: (1) Copy of both sides of your Green Card (Permanent Resident Card), (2) Two 2\"×2\" passport-style photos with white background. Additional documents depend on your situation — divorce decrees if previously married, marriage certificate if applying under the 3-year rule.",
    },
    {
      question: "What are the USCIS photo requirements?",
      staticAnswer:
        "2\"×2\" color photo, white or off-white background, taken within the last 6 months, full face, front view, eyes open, no glasses. Most pharmacies (CVS, Walgreens) offer these for $10–15. Write your A-Number lightly in pencil on the back of each photo.",
    },
    {
      question: "What if I don't have my divorce decree?",
      staticAnswer:
        "Contact the court where the divorce was finalized to request a certified copy. If the divorce happened abroad, you may need an official copy from that country plus a certified translation. If you genuinely cannot obtain it, include a written explanation with your application.",
    },
  ],

  oath: [
    {
      question: "What happens if I answer \"No\" to bearing arms?",
      staticAnswer:
        "Answering \"No\" does not automatically disqualify you. USCIS accommodates applicants with sincere religious or conscientious objections. You'll be asked to explain at your interview and may need to provide documentation (e.g., a letter from your religious organization). Many applicants with religious objections have been successfully naturalized.",
    },
    {
      question: "What is noncombatant military service?",
      staticAnswer:
        "Noncombatant service means military roles that don't involve direct combat — for example: military medic, chaplain, supply and logistics, administrative roles. The question asks if you're willing to serve in such a role if required by law.",
    },
    {
      question: "What does \"work of national importance under civilian direction\" mean?",
      staticAnswer:
        "This refers to non-military work deemed critical to national interests — emergency public health, civil defense, disaster response, etc. — directed by civilian government. It applies only if you object to all forms of service, including civilian roles.",
    },
  ],

  contact: [
    {
      question: "Does the signature date have to be today?",
      staticAnswer:
        "No — enter the date you intend to actually sign the printed form before mailing it. If you're generating the packet now but plan to mail it next week, use that future date. Don't pre-date or backdate beyond your plans.",
    },
    {
      question: "Which phone number should I list as the daytime number?",
      staticAnswer:
        "List the number where USCIS can reach you between 9am and 5pm — typically your mobile or work number. USCIS uses this to schedule and confirm interview appointments.",
    },
  ],

  "fee-reduction": [
    {
      question: "How do I know if my income qualifies for a reduced fee?",
      staticAnswer:
        "The reduced fee is available if your household income is at or below 400% of the Federal Poverty Guidelines (FPG). For a family of 4 in 2025, that's approximately $129,000/year. Check the current thresholds at aspe.hhs.gov. Count everyone in your household including yourself, your spouse, and dependents.",
    },
    {
      question: "What counts as household income for this calculation?",
      staticAnswer:
        "Gross income before taxes from all sources: wages, self-employment income, Social Security benefits, pensions, rental income, child support received, and other regular income. Do not include one-time amounts like an inheritance.",
    },
  ],

  interpreter: [
    {
      question: "What counts as using an interpreter for the N-400?",
      staticAnswer:
        "If someone verbally translated the form questions or instructions from English into your language while you were filling it out — that's using an interpreter and they should be listed. If you filled out the form entirely in English on your own (even with a dictionary), answer \"No\".",
    },
    {
      question: "Does using a family member as an interpreter count?",
      staticAnswer:
        "Yes — a spouse, child, sibling, or friend who translated for you counts as an interpreter. List their name and contact information. They don't need professional credentials.",
    },
    {
      question: "Will having an interpreter affect my English language test at the interview?",
      staticAnswer:
        "No — listing an interpreter on Part 15 only refers to help filling out the form. It has no bearing on your English language requirement for the naturalization interview. Those are separate.",
    },
  ],

  preparer: [
    {
      question: "Does using GetCitizen AI count as having a preparer?",
      staticAnswer:
        "No — if you entered your own information and made your own decisions using GetCitizen AI as a tool, you are considered to have prepared the form yourself. Answer \"No\" to the preparer question. If an attorney or other person directed what to write, list them as the preparer.",
    },
    {
      question: "If my lawyer helped me, are they the preparer?",
      staticAnswer:
        "Yes — if an immigration attorney or other person helped you decide what to enter, they should be listed as the preparer in Part 16. Your attorney's firm name goes in the \"Business or organization\" field.",
    },
  ],

  checkout: [
    {
      question: "Who receives the $710 filing fee?",
      staticAnswer:
        "The $710 goes directly to USCIS (U.S. Citizenship and Immigration Services) as their official N-400 filing fee. This is separate from GetCitizen AI's service fee. USCIS charges this fee to every applicant to process their naturalization application.",
    },
    {
      question: "What do I get after I pay?",
      staticAnswer:
        "After payment, you can generate and download a completed, print-ready N-400 PDF with all your information filled in. You then print it, sign it, and mail it to USCIS with your supporting documents. GetCitizen AI does not file on your behalf.",
    },
    {
      question: "Can I preview the form before paying?",
      staticAnswer:
        "The Review step shows a summary of your application completeness. The full downloadable PDF packet is unlocked after payment, since the completed form is the deliverable you're paying for.",
    },
    {
      question: "Is this a one-time payment?",
      staticAnswer:
        "The USCIS filing fee ($710) is a one-time payment per application. GetCitizen AI's service fee is also a one-time charge for generating your N-400 packet. There are no subscriptions or recurring charges.",
    },
  ],

  review: [
    {
      question: "What do I do after downloading my N-400 packet?",
      staticAnswer:
        "1. Print on 8.5\"×11\" white paper, one side only. 2. Sign and date Part 14 in ink (black or blue). 3. Gather supporting documents (Green Card copy, 2 passport photos, any required extras). 4. Find the correct mailing address for your state at uscis.gov/n-400. 5. Mail everything in one envelope — use a trackable service. USCIS will send a receipt notice within a few weeks.",
    },
    {
      question: "Where do I mail my completed N-400?",
      staticAnswer:
        "Mailing addresses depend on your state and whether you use USPS or a courier (FedEx/UPS). Find the correct address at uscis.gov/n-400 under \"Filing Tips\". Do not use an old address — USCIS Lockbox addresses change periodically.",
    },
    {
      question: "How long will USCIS take to process my N-400?",
      staticAnswer:
        "Processing times vary significantly by field office — typically several months to over a year. Check current processing times at egov.uscis.gov/processing-times after you file. Your I-797 receipt notice will show your case number for tracking.",
    },
    {
      question: "What should I include in my mailing envelope?",
      staticAnswer:
        "Required for all applicants: signed N-400 form, copy of both sides of your Green Card, and two passport-style photos (write A-Number lightly on back in pencil). Additional documents based on your situation: marriage certificate (3-year rule), divorce decree(s), any criminal court records. Keep copies of everything you mail.",
    },
  ],
};

/** Default chips shown when the wizard step is unknown or not in the map. */
export const DEFAULT_CHIPS: ChipDef[] = [
  {
    question: "What is an A-Number?",
    staticAnswer:
      "Your A-Number (Alien Registration Number) is printed on the front of your Green Card as \"USCIS#\". It's 7–9 digits — enter just the digits on the N-400.",
  },
  {
    question: "How does a long trip affect my application?",
    staticAnswer:
      "A single trip of 180+ days may affect your continuous residence. List all trips accurately — the rules engine will flag any concerns.",
  },
  {
    question: "What happens after I download my N-400?",
    staticAnswer:
      "Print it, sign it in ink, attach your Green Card copy and 2 passport photos, and mail to the USCIS Lockbox address for your state. See uscis.gov/n-400 for the correct address.",
  },
  {
    question: "Does using GetCitizen AI count as having a preparer?",
    staticAnswer:
      "No — if you entered your own information, you prepared the form yourself. Answer \"No\" to the preparer question unless an attorney or other person directed your answers.",
  },
];
